import React, { useState } from 'react';
import { Target, Plus, Calendar, DollarSign, TrendingUp, Edit, Trash2, X } from 'lucide-react';

const FinancialGoalsView = ({ formatCurrency, goals, setGoals }) => {

  const [showNewGoalForm, setShowNewGoalForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [newGoal, setNewGoal] = useState({
    name: '',
    targetAmount: '',
    currentAmount: '',
    deadline: '',
    monthlyContribution: ''
  });

  const calculateProgress = (current, target) => (current / target) * 100;
  const calculateMonthsLeft = (deadline) => {
    const now = new Date();
    const target = new Date(deadline);
    return Math.max(0, Math.ceil((target - now) / (1000 * 60 * 60 * 24 * 30)));
  };
  const calculateProjectedCompletion = (current, target, monthly) => {
    if (monthly <= 0) return null;
    const remaining = target - current;
    const months = Math.ceil(remaining / monthly);
    const completionDate = new Date();
    completionDate.setMonth(completionDate.getMonth() + months);
    return completionDate.toLocaleDateString('es-CL', { year: 'numeric', month: 'long' });
  };

  // Handle goal operations
  const handleCreateGoal = (e) => {
    e.preventDefault();
    if (!newGoal.name || !newGoal.targetAmount || !newGoal.deadline) return;

    const goal = {
      id: Date.now(),
      name: newGoal.name,
      targetAmount: parseFloat(newGoal.targetAmount),
      currentAmount: parseFloat(newGoal.currentAmount) || 0,
      deadline: newGoal.deadline,
      monthlyContribution: parseFloat(newGoal.monthlyContribution) || 0
    };

    setGoals([...goals, goal]);
    setNewGoal({ name: '', targetAmount: '', currentAmount: '', deadline: '', monthlyContribution: '' });
    setShowNewGoalForm(false);
  };

  const handleEditGoal = (goal) => {
    setEditingGoal(goal);
    setNewGoal({
      name: goal.name,
      targetAmount: goal.targetAmount.toString(),
      currentAmount: goal.currentAmount.toString(),
      deadline: goal.deadline,
      monthlyContribution: goal.monthlyContribution.toString()
    });
  };

  const handleUpdateGoal = (e) => {
    e.preventDefault();
    if (!newGoal.name || !newGoal.targetAmount || !newGoal.deadline) return;

    const updatedGoals = goals.map(goal =>
      goal.id === editingGoal.id
        ? {
            ...goal,
            name: newGoal.name,
            targetAmount: parseFloat(newGoal.targetAmount),
            currentAmount: parseFloat(newGoal.currentAmount) || 0,
            deadline: newGoal.deadline,
            monthlyContribution: parseFloat(newGoal.monthlyContribution) || 0
          }
        : goal
    );

    setGoals(updatedGoals);
    setEditingGoal(null);
    setNewGoal({ name: '', targetAmount: '', currentAmount: '', deadline: '', monthlyContribution: '' });
  };

  const handleDeleteGoal = (goalId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este objetivo?')) {
      setGoals(goals.filter(goal => goal.id !== goalId));
    }
  };

  const handleCancelEdit = () => {
    setEditingGoal(null);
    setNewGoal({ name: '', targetAmount: '', currentAmount: '', deadline: '', monthlyContribution: '' });
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-[#041627] mb-2 font-headline">Objetivos Financieros</h1>
          <p className="text-sm text-slate-500">Gestiona tus metas de ahorro con seguimiento automático.</p>
        </div>
        <button
          onClick={() => setShowNewGoalForm(true)}
          className="bg-[#041627] text-white px-6 py-3 rounded-xl font-bold flex items-center gap-2 hover:opacity-90"
        >
          <Plus size={20} /> Nuevo Objetivo
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {goals.map(goal => {
          const progress = calculateProgress(goal.currentAmount, goal.targetAmount);
          const monthsLeft = calculateMonthsLeft(goal.deadline);
          const projectedCompletion = calculateProjectedCompletion(goal.currentAmount, goal.targetAmount, goal.monthlyContribution);

          return (
            <div key={goal.id} className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 relative group">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#041627]/10 rounded-lg">
                    <Target size={20} className="text-[#041627]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#041627]">{goal.name}</h3>
                    <p className="text-xs text-slate-500 flex items-center gap-1">
                      <Calendar size={12} /> {monthsLeft} meses restantes
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleEditGoal(goal)}
                    className="p-2 text-slate-400 hover:text-[#041627] hover:bg-slate-100 rounded-lg transition-colors"
                    title="Editar objetivo"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleDeleteGoal(goal.id)}
                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Eliminar objetivo"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-600">Progreso</span>
                    <span className="font-bold text-[#041627]">{progress.toFixed(1)}%</span>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#041627] transition-all duration-500"
                      style={{ width: `${Math.min(progress, 100)}%` }}
                    ></div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Actual</p>
                    <p className="font-bold text-[#041627]">{formatCurrency(goal.currentAmount)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Meta</p>
                    <p className="font-bold text-[#041627]">{formatCurrency(goal.targetAmount)}</p>
                  </div>
                </div>

                <div className="pt-2 border-t border-slate-100">
                  <p className="text-xs text-slate-500 mb-1">Aporte mensual</p>
                  <p className="font-bold text-[#006d37] flex items-center gap-1">
                    <TrendingUp size={14} /> {formatCurrency(goal.monthlyContribution)}
                  </p>
                  {projectedCompletion && (
                    <p className="text-xs text-slate-400 mt-1">
                      Completado proyectado: {projectedCompletion}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {(showNewGoalForm || editingGoal) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-[#041627]">
                {editingGoal ? 'Editar Objetivo' : 'Nuevo Objetivo Financiero'}
              </h3>
              <button
                onClick={() => {
                  setShowNewGoalForm(false);
                  handleCancelEdit();
                }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={20} className="text-slate-400" />
              </button>
            </div>
            <form onSubmit={editingGoal ? handleUpdateGoal : handleCreateGoal} className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-[#44474c] block mb-2">Nombre del objetivo</label>
                <input
                  type="text"
                  placeholder="ej: Vacaciones Europa"
                  value={newGoal.name}
                  onChange={(e) => setNewGoal({...newGoal, name: e.target.value})}
                  className="w-full bg-[#f2f4f7] border-none rounded-lg p-3 text-sm"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-[#44474c] block mb-2">Monto meta (CLP)</label>
                <input
                  type="number"
                  placeholder="5000000"
                  value={newGoal.targetAmount}
                  onChange={(e) => setNewGoal({...newGoal, targetAmount: e.target.value})}
                  className="w-full bg-[#f2f4f7] border-none rounded-lg p-3 text-sm"
                  min="0"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-[#44474c] block mb-2">Monto actual (CLP)</label>
                <input
                  type="number"
                  placeholder="0"
                  value={newGoal.currentAmount}
                  onChange={(e) => setNewGoal({...newGoal, currentAmount: e.target.value})}
                  className="w-full bg-[#f2f4f7] border-none rounded-lg p-3 text-sm"
                  min="0"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-[#44474c] block mb-2">Fecha límite</label>
                <input
                  type="date"
                  value={newGoal.deadline}
                  onChange={(e) => setNewGoal({...newGoal, deadline: e.target.value})}
                  className="w-full bg-[#f2f4f7] border-none rounded-lg p-3 text-sm"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-[#44474c] block mb-2">Aporte mensual (CLP)</label>
                <input
                  type="number"
                  placeholder="150000"
                  value={newGoal.monthlyContribution}
                  onChange={(e) => setNewGoal({...newGoal, monthlyContribution: e.target.value})}
                  className="w-full bg-[#f2f4f7] border-none rounded-lg p-3 text-sm"
                  min="0"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowNewGoalForm(false);
                    handleCancelEdit();
                  }}
                  className="flex-1 bg-slate-100 text-slate-600 py-3 rounded-lg font-bold"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-[#041627] text-white py-3 rounded-lg font-bold hover:opacity-90"
                >
                  {editingGoal ? 'Actualizar Objetivo' : 'Crear Objetivo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialGoalsView;